<script setup>
/**
 * RED-310 — "Kaardi filtrid" zone: a ToggleSwitch that decides whether the
 * map's current filters travel with the request, plus a Chip row echoing the
 * inherited ones. The report fixes vara liik (KOM, or KASI + hoonestamata for
 * RED-316 land — muted chip); the map's date range and viewport are replaced
 * by the drawer's own period / location, so they never appear here (see
 * store.forcedMapFilterKeys).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mapFilterLabel, mapFilterValue } from '@/services/mapFilterText'
import { useAnalyticsStore } from '@/stores/analyticsStore'

const { t, te } = useI18n()
const store = useAnalyticsStore()

const forcedLabel = computed(() => {
  const key = `messages.transactions.analytics.form.map_filters_forced_by_report.${store.form.report.replaceAll('-', '_')}`
  return te(key) ? t(key) : t('messages.transactions.analytics.form.map_filters_forced')
})

const chips = computed(() =>
  store.inheritedMapFilters.map(filter => {
    const label = mapFilterLabel(filter.key, { t, te })
    const value = mapFilterValue(filter, { t })
    return { key: filter.key, label: value ? `${label}: ${value}` : label }
  }),
)
</script>

<template>
  <div class="flex flex-col gap-2">
    <label class="text-sm font-medium text-gray-700">
      {{ $t('messages.transactions.analytics.form.map_filters') }}
    </label>
    <div class="flex items-center gap-2">
      <ToggleSwitch
        v-model="store.form.useMapFilters"
        input-id="txn-analytics-map-filters"
        data-testid="txn-analytics-map-filters"
      />
      <label for="txn-analytics-map-filters" class="text-sm text-gray-700 cursor-pointer">
        {{ $t('messages.transactions.analytics.form.map_filters_use') }}
      </label>
    </div>
    <div class="flex flex-wrap items-center gap-1.5 min-h-7" data-testid="txn-analytics-map-filter-chips">
      <Chip
        :label="forcedLabel"
        class="text-xs bg-gray-100 text-gray-500"
        data-testid="txn-analytics-map-filter-forced"
      />
      <template v-if="store.form.useMapFilters">
        <Chip
          v-for="chip in chips"
          :key="chip.key"
          :label="chip.label"
          class="text-xs"
          :data-testid="`txn-analytics-map-filter-${chip.key}`"
        />
        <span v-if="!chips.length" class="text-xs text-gray-500">
          {{ $t('messages.transactions.analytics.form.map_filters_none') }}
        </span>
      </template>
      <span v-else class="text-xs text-gray-500">
        {{ $t('messages.transactions.analytics.form.map_filters_off') }}
      </span>
    </div>
  </div>
</template>
